/**
 * Kit UI de l'administration — port du design Lovable (components/admin/ui.tsx).
 * DA « projection privée » : cartes charbon sans ombre, badges pilule,
 * montants en dégradé signature, tables denses.
 */
import type { ReactNode } from 'react';
import { Check, TriangleAlert, X } from 'lucide-react';

export function PageTitle({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="display-title text-[32px] text-blanc sm:text-[40px]">{title}</h1>
        {subtitle && <p className="mt-3 max-w-[620px] text-base leading-relaxed text-fumee">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-[10px] bg-charbon p-5 ${className}`}>{children}</div>;
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <p className="label-caps text-fumee">{children}</p>;
}

export function Metric({
  label,
  value,
  detail,
  ok,
}: {
  label: string;
  value: string;
  detail?: string;
  ok?: boolean;
}) {
  return (
    <Card>
      <SectionLabel>{label}</SectionLabel>
      <p className="display-title mt-3 text-[30px] text-blanc">{value}</p>
      {detail && (
        <p className="mt-2 flex items-center gap-1.5 text-[13px] text-fumee">
          {ok === true && <Check size={13} strokeWidth={1.5} className="text-blanc" />}
          {ok === false && <TriangleAlert size={13} strokeWidth={1.5} className="text-blanc" />}
          {detail}
        </p>
      )}
    </Card>
  );
}

export function Badge({ children, filled = false }: { children: ReactNode; filled?: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.05em] ${
        filled ? 'bg-blanc text-void' : 'border border-graphite text-fumee'
      }`}
    >
      {children}
    </span>
  );
}

export function GradientAmount({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`text-match-gradient font-medium ${className}`}>{children}</span>;
}

export function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-[10px] bg-charbon">
      <table className="admin-table">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Bar({ value, max = 100 }: { value: number; max?: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-graphite">
      <div
        className="bg-match-gradient h-full rounded-full"
        style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
      />
    </div>
  );
}

export function Toggle({ on }: { on: boolean }) {
  return (
    <span
      className={`inline-flex h-5 w-9 items-center rounded-full border border-graphite p-0.5 ${
        on ? 'bg-match-gradient' : 'bg-transparent'
      }`}
      aria-label={on ? 'Actif' : 'Inactif'}
    >
      <span className={`h-3.5 w-3.5 rounded-full bg-blanc transition-transform duration-200 ${on ? 'translate-x-4' : ''}`} />
    </span>
  );
}

export function Btn({
  children,
  onClick,
  variant = 'ghost',
  className = '',
  type = 'button',
  disabled = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'ghost' | 'primary';
  className?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  const base =
    variant === 'primary'
      ? 'btn-creme !min-h-0 px-5 py-2 text-[14px]'
      : 'rounded-full border border-graphite px-4 py-2 text-[14px] text-fumee transition-colors hover:text-blanc';
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${className} disabled:opacity-50`}>
      {children}
    </button>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-void/80" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-[520px] overflow-y-auto rounded-t-[16px] border border-graphite bg-charbon p-5 sm:rounded-[14px] sm:p-6">
        <div className="mb-5 flex items-start justify-between gap-4">
          <h2 className="text-[18px] font-light text-blanc">{title}</h2>
          <button onClick={onClose} aria-label="Fermer" className="text-fumee hover:text-blanc">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Input({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="label-caps block text-fumee">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="field-input mt-2 !min-h-[42px] !py-2 !text-sm"
      />
    </label>
  );
}

export function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly { valeur: string; texte: string }[];
}) {
  return (
    <label className="block">
      <span className="label-caps block text-fumee">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="field-input mt-2 !min-h-[42px] !py-2 !text-sm"
      >
        {options.map((o) => (
          <option key={o.valeur} value={o.valeur} className="bg-charbon">
            {o.texte}
          </option>
        ))}
      </select>
    </label>
  );
}

export function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <label className="block">
      {label && <span className="label-caps block text-fumee">{label}</span>}
      <textarea
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="field-input mt-2 !min-h-0 !py-2 !text-sm"
      />
    </label>
  );
}

export function Chip({
  children,
  active = false,
  onClick,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border border-graphite px-4 py-2 text-[13px] transition-colors duration-200 ${
        active ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'
      }`}
    >
      {children}
    </button>
  );
}

export const eur = (n: number) =>
  n.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' });
